import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Input,
  InputGroup,
  Table,
  Button,
  DOMHelper,
  Stack,
  DateRangePicker,
  Tag,
  Modal,
  Whisper,
  IconButton,
  Message,
  useToaster,
  Pagination,
  Panel,
  Loader
} from 'rsuite';
import SearchIcon from '@rsuite/icons/Search';
import ExportIcon from '@rsuite/icons/Export';
import { MdFilterList, MdDelete, MdVisibility, MdInfoOutline } from 'react-icons/md';
import { Icon } from '@rsuite/icons';
import DrawerView from './DrawerView';
import DetailsDrawerView from './DetailsDrawerView';
import NotificationSettingsDrawer from '../../NotificationSettingsDrawer';
import { useDispatch, useSelector } from 'react-redux';
import {
  getRequests,
  deleteRequest,
  fetchRequestsWithoutDispatch
} from '@/services/request.service';
import { getUsersWithoutDispatch } from '@/services/user.service'; // Import the service
import { format } from 'date-fns';
import { Tooltip } from 'rsuite';
import * as XLSX from 'xlsx';
const { Column, HeaderCell, Cell } = Table;
const { getHeight } = DOMHelper;

const DataTable = () => {
  const dispatch = useDispatch();
  const toaster = useToaster();
  const { t } = useTranslation();
  const {
    data: requests,
    pagination,
    loading: requestLoading
  } = useSelector((state: any) => state.request);
  const [allUsers, setAllUsers] = useState([]);
  const [showDrawer, setShowDrawer] = useState(false);
  const [showNotificationDrawer, setShowNotificationDrawer] = useState(false);
  const [limit, setLimit] = useState(20);
  const [page, setPage] = useState(1);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [debouncedSearchKeyword, setDebouncedSearchKeyword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [filters, setFilters] = useState({
    userId: '',
    validatorId: '',
    status: '',
    type: ''
  });
  const [dateRange, setDateRange] = useState(() => {
    const today = new Date();
    const start = new Date(today.getFullYear(), today.getMonth(), 1);
    const end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    end.setHours(23, 59, 59, 999);
    return [start, end];
  });
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showDetailsDrawer, setShowDetailsDrawer] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [loadingExport, setLoadingExport] = useState(false);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchKeyword(searchKeyword);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [searchKeyword]);

  useEffect(() => {
    // Fetch all users and filter out forgotten: true
    const fetchData = async () => {
      const userData = await getUsersWithoutDispatch();
      const filteredUsers = userData?.filter(user => !user.forgotten);
      setAllUsers(filteredUsers);
    };

    fetchData();
  }, []);

  useEffect(() => {
    setIsLoading(true);
    if (dateRange && dateRange[0]) {
      const startDate = new Date(dateRange[0]);
      startDate.setHours(0, 0, 0, 0);
      const endDate = dateRange[1] ? new Date(dateRange[1]) : new Date(dateRange[0]);
      endDate.setHours(23, 59, 59, 999);
      dispatch(
        getRequests(
          format(startDate, 'yyyy-MM-dd'),
          format(endDate, 'yyyy-MM-dd'),
          page,
          limit,
          debouncedSearchKeyword,
          filters
        )
      );
      setIsLoading(false);
    }
  }, [dateRange, page, limit, debouncedSearchKeyword, filters, dispatch]);

  const handleChangeLimit = (dataKey: number) => {
    setPage(1);
    setLimit(dataKey);
  };

  const filteredData = requests || [];

  const handleDetails = (rowData: any, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedRequest(rowData);
    setShowDetailsDrawer(true);
  };

  const handleDelete = (rowData: any, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedRequest(rowData);
    setShowDeleteModal(true);
  };

  const confirmDelete = () => {
    if (selectedRequest) {
      dispatch(deleteRequest(selectedRequest._id._id))
        .then(() => {
          setShowDeleteModal(false);
          toaster.push(
            <Message type="success" duration={3000}>
              {t('Request deleted successfully')}
            </Message>,
            { placement: 'topCenter' }
          );
        })
        .catch(() => {
          setShowDeleteModal(false);
          toaster.push(
            <Message type="error" duration={3000}>
              {t('Error deleting request')}
            </Message>,
            { placement: 'topCenter' }
          );
        });
    }
  };

  const downloadExcel = (data: any[], name: string) => {
    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
    XLSX.writeFile(workbook, `${name}.xlsx`);
  };

  const handleExport = async () => {
    setLoadingExport(true);

    const result = await fetchRequestsWithoutDispatch(
      dateRange[0],
      dateRange[1],
      null,
      null,
      searchKeyword,
      filters
    );
    // console.log('result', result);
    const dataToExport = result.data;
    const toExport = dataToExport.map(item => ({
      'Full Name': `${item.idSender?.firstname || ''} ${item.idSender?.lastname || ''}`,
      'Registration Number': item.idSender?.registrationNumber || '',
      Type: item._id?.type || '',
      'Leave Balance': item.balance?.LeaveBalance || '',
      'Start Date': `${format(new Date(item.operations[0]?.date_debut), 'dd-MM-yyyy')}`,
      'Start Slot': item.operations[0]?.slot_debut,
      'End Date': ` ${format(new Date(item.operations[0]?.date_fin), 'dd-MM-yyyy')} `,
      'End Slot': item.operations[0]?.slot_fin,
      Validator: `${item.idReciever?.firstname || ''} ${item.idReciever?.lastname || ''}`,
      'Counted Days': item.operations[0]?.NB_BalanceDays,
      Status: item._id?.status || '',
      'Created At': new Date(item._id?.createdAt).toLocaleString()
    }));
    downloadExcel(
      toExport,
      `leave-requests_${format(dateRange[0], 'dd-MM-yyyy')}_${format(dateRange[1], 'dd-MM-yyyy')}`
    );
    setLoadingExport(false);
  };

  return (
    <>
      <style>
        {`
          .rs-picker-daterange-menu .rs-calendar-header-year,
          .rs-picker-daterange-menu .rs-calendar-header-year-btn {
            display: none !important;
          }
        `}
      </style>
      <Stack className="table-toolbar" justifyContent="space-between">
        <DateRangePicker
          appearance="default"
          placeholder={t('Select Date Range')}
          style={{ width: 230 }}
          value={dateRange}
          onChange={setDateRange}
          editable={false}
        />

        <Stack spacing={6}>
          <InputGroup inside>
            <Input placeholder={t('Search')} value={searchKeyword} onChange={setSearchKeyword} />
            <InputGroup.Addon>
              <SearchIcon />
            </InputGroup.Addon>
          </InputGroup>
          <Button appearance="primary" onClick={() => setShowDrawer(true)}>
            <MdFilterList style={{ marginRight: 8 }} /> {t('Filters')}
          </Button>
          <Button
            appearance="primary"
            onClick={handleExport}
            loading={loadingExport}
            disabled={loadingExport}
          >
            <ExportIcon style={{ marginRight: 8 }} /> {t('Export')}
          </Button>
          <Button appearance="primary" onClick={() => setShowNotificationDrawer(true)}>
            <Icon as={MdInfoOutline} style={{ marginRight: 8 }} /> {t('Notification Settings')}
          </Button>
        </Stack>
      </Stack>

      <Panel bodyFill>
        {isLoading ? (
          <Loader center content="Loading..." />
        ) : (
          <>
            <Table
              height={Math.max(getHeight(window) - 200, 400)}
              data={filteredData}
              loading={requestLoading}
              rowHeight={80}
              wordWrap="break-word"
            >
              <Column flexGrow={4}>
                <HeaderCell>{t('First Name & Last Name')}</HeaderCell>
                <Cell>
                  {rowData => `${rowData.idSender?.firstname} ${rowData.idSender?.lastname}`}
                </Cell>
              </Column>

              <Column flexGrow={3}>
                <HeaderCell>{t('Registration Number')}</HeaderCell>
                <Cell>{rowData => rowData.idSender?.registrationNumber}</Cell>
              </Column>

              <Column flexGrow={3}>
                <HeaderCell>{t('Type')}</HeaderCell>
                <Cell>{rowData => rowData._id?.type}</Cell>
              </Column>

              <Column flexGrow={3}>
                <HeaderCell>{t('Period')}</HeaderCell>
                <Cell>
                  {rowData =>
                    `${format(new Date(rowData.operations[0]?.date_debut), 'dd-MM-yyyy')} ${
                      rowData.operations[0]?.slot_debut
                    } To ${format(new Date(rowData.operations[0]?.date_fin), 'dd-MM-yyyy')} ${
                      rowData.operations[0]?.slot_fin
                    } `
                  }
                </Cell>
              </Column>

              <Column flexGrow={3}>
                <HeaderCell>{t('Balance')}</HeaderCell>
                <Cell>{rowData => rowData.balance?.LeaveBalance}</Cell>
              </Column>

              <Column flexGrow={4}>
                <HeaderCell>{t('Validator')}</HeaderCell>
                <Cell>
                  {rowData => `${rowData.idReciever?.firstname} ${rowData.idReciever?.lastname}`}
                </Cell>
              </Column>

              <Column flexGrow={3}>
                <HeaderCell>{t('Status')}</HeaderCell>
                <Cell>
                  {rowData => (
                    <Tag
                      color={
                        rowData._id?.status === 'accepted'
                          ? 'green'
                          : rowData._id?.status === 'pending'
                          ? 'orange'
                          : 'red'
                      }
                    >
                      {t(rowData._id?.status)}
                    </Tag>
                  )}
                </Cell>
              </Column>

              <Column flexGrow={3}>
                <HeaderCell>{t('Created at')}</HeaderCell>
                <Cell>{rowData => new Date(rowData._id?.createdAt).toLocaleString()}</Cell>
              </Column>

              <Column width={100} fixed="right">
                <HeaderCell>{t('Actions')}</HeaderCell>
                <Cell>
                  {rowData => (
                    <Stack spacing={6}>
                      <Whisper
                        placement="top"
                        trigger="hover"
                        speaker={<Tooltip>{t('View Details')}</Tooltip>}
                      >
                        <IconButton
                          size="xs"
                          icon={<Icon as={MdInfoOutline} />}
                          onClick={e => handleDetails(rowData, e)}
                          id={`details-${rowData._id?._id}`}
                        />
                      </Whisper>
                      <Whisper
                        placement="top"
                        trigger="hover"
                        speaker={<Tooltip>{t('Delete Request')}</Tooltip>}
                      >
                        <IconButton
                          size="xs"
                          icon={<Icon as={MdDelete} style={{ color: 'red' }} />}
                          onClick={e => handleDelete(rowData, e)}
                          id={`delete-${rowData._id?._id}`}
                        />
                      </Whisper>
                    </Stack>
                  )}
                </Cell>
              </Column>
            </Table>
            <div style={{ padding: 20 }}>
              <Pagination
                prev
                next
                first
                last
                ellipsis
                boundaryLinks
                maxButtons={5}
                size="xs"
                layout={['total', '-', 'limit', '|', 'pager', 'skip']}
                total={pagination?.total || 0}
                limitOptions={[10, 20, 30, 50]}
                limit={limit}
                activePage={page}
                onChangePage={setPage}
                onChangeLimit={handleChangeLimit}
              />
            </div>
          </>
        )}
      </Panel>

      <DrawerView
        open={showDrawer}
        onClose={() => setShowDrawer(false)}
        onFiltersChange={setFilters}
        onResetFilters={() =>
          setFilters({
            userId: '',
            validatorId: '',
            status: '',
            type: ''
          })
        }
        currentFilters={filters}
        users={allUsers}
        validators={allUsers} // Use the same filtered users for validators
        usersLoading={isLoading}
      />

      {showDetailsDrawer && (
        <DetailsDrawerView
          open={showDetailsDrawer}
          selectedRequest={selectedRequest}
          onClose={() => setShowDetailsDrawer(false)}
        />
      )}

      <NotificationSettingsDrawer
        open={showNotificationDrawer}
        onClose={() => setShowNotificationDrawer(false)}
        type="Leave"
      />

      <Modal size="xs" open={showDeleteModal} onClose={() => setShowDeleteModal(false)}>
        <Modal.Header>
          <Modal.Title>{t('Confirm Delete')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>{t('Are you sure you want to delete this request?')}</Modal.Body>
        <Modal.Footer>
          <Button onClick={confirmDelete} appearance="primary" color="red">
            {t('Yes')}
          </Button>
          <Button onClick={() => setShowDeleteModal(false)} appearance="subtle">
            {t('No')}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default DataTable;
